import React, { useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';
import { complianceAPI } from '../lib/api';

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletionStatus, setDeletionStatus] = useState(null);
  const [deletionStatusChecked, setDeletionStatusChecked] = useState(false);

  async function signup(companyName, email, password) {
    const response = await authAPI.register({
      companyName,
      email,
      password
    });
    return response.data;
  }

  async function login(email, password) {
    const response = await authAPI.login({
      email,
      password
    });

    const { token, user } = response.data;
    // Normalize user object to always have 'id' field
    const normalizedUser = {
      ...user,
      id: user.id || user._id
    };
    localStorage.setItem('token', token);
    setCurrentUser(normalizedUser);
    // Check deletion status immediately after login
    checkDeletionStatusBackground(true);

    return response.data;
  }

  function logout() {
    localStorage.removeItem('token');
    setCurrentUser(null);
    setDeletionStatus(null);
    setDeletionStatusChecked(false);
  }

  function getCurrentUser() {
    return currentUser;
  }

  function updateCurrentUser(userData) {
    setCurrentUser(prevUser => ({
      ...prevUser,
      ...userData
    }));
  }

  // Separate function to check deletion status in background
  async function checkDeletionStatusBackground(immediate = false) {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      // Create a promise that rejects after 3 seconds
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Deletion status check timeout')), 3000)
      );
      
      // Race the API call against the timeout
      const response = await Promise.race([
        complianceAPI.getUserDeletionStatus(),
        timeoutPromise
      ]);
      
      // Handle both possible response formats (with and without data wrapper)
      let actualData;
      if (response.data && response.data.data) {
        // API gateway wrapped format
        actualData = response.data.data;
      } else if (response.data) {
        // Direct format
        actualData = response.data;
      } else {
        // No data
        setDeletionStatus(null);
        setDeletionStatusChecked(true);
        return;
      }
      
      if (actualData.hasRequest && actualData.status === 'pending') {
        setDeletionStatus(actualData);
      } else {
        setDeletionStatus(null);
      }
      setDeletionStatusChecked(true);
    } catch (error) {
      console.error('Failed to load deletion request status:', error);
      // Don't change the current deletion status on timeout/error
      setDeletionStatusChecked(true);
    }
  }

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Only fetch profile initially, don't block on deletion status
      authAPI.getProfile()
        .then(response => {
          // Normalize user object to always have 'id' field
          const userData = {
            ...response.data,
            id: response.data.id || response.data._id
          };
          setCurrentUser(userData);
          setLoading(false);

          // Check deletion status in background
          checkDeletionStatusBackground();
        })
        .catch((error) => {
          console.error('Error fetching profile:', error); // Debug logging
          localStorage.removeItem('token');
          setLoading(false);
        });
    } else {
      setLoading(false);
    }

    // Set up periodic deletion status check every 30 seconds
    const interval = setInterval(() => {
      if (token) {
        checkDeletionStatusBackground();
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    logout,
    getCurrentUser,
    updateCurrentUser,
    deletionStatus,
    deletionStatusChecked,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}